import PropTypes from "prop-types"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useInView } from "react-hook-inview"
import { easeOut, longAnim } from "utils/gsapAnims"
import { separateChildrenFromLine } from "utils/separateChildTagsFromLine"
import "./textLockupV2.scss"

/**
 * TextLockupV2 - A simplified wrapper component that applies GSAP animations to text children
 * Usage: <TextLockupV2>Your text here</TextLockupV2>
 */
const TextLockupV2 = ({ children, className = "", shouldAnimateIn = true }) => {
  const [ref, inView] = useInView({ threshold: 0.65, unobserveOnEnter: true })
  const contentRef = useRef()
  const [split, setSplit] = useState(null)

  const initAnimation = useCallback(() => {
    if (
      !shouldAnimateIn ||
      typeof window === "undefined" ||
      !window.gsap ||
      !window.SplitText ||
      !contentRef.current
    )
      return

    const gsap = window.gsap
    const SplitText = window.SplitText

    // Split the text into lines and words
    const splitText = new SplitText(contentRef.current, {
      type: "lines, words",
      linesClass: "line-v2",
      tag: "span",
    })

    // Set initial state for each line's children
    splitText.lines.forEach(line => {
      const [spans, otherTags] = separateChildrenFromLine(line)
      gsap.set([...spans, ...otherTags], { opacity: 0, y: "1em" })
    })

    setSplit(splitText)

    // Make the container visible
    gsap.set(ref.current, { opacity: 1 })
  }, [shouldAnimateIn, ref])

  useEffect(() => {
    initAnimation()
  }, [initAnimation])

  useEffect(() => {
    if (
      !shouldAnimateIn ||
      !inView ||
      !split ||
      !split.lines ||
      typeof window === "undefined" ||
      !window.gsap
    )
      return

    const gsap = window.gsap
    const delay = 0.033
    const duration = longAnim

    // Animate each line with a staggered delay
    split.lines.forEach((line, index) => {
      const [spans, otherTags] = separateChildrenFromLine(line)
      const isLastLine = index === split.lines.length - 1

      gsap.to([...spans, ...otherTags], {
        delay: delay * index,
        duration,
        ease: easeOut,
        opacity: 1,
        y: 0,
        onComplete: isLastLine
          ? () => {
              // Add a small delay before changing display to ensure all animations are complete
              setTimeout(() => {
                split.lines.forEach(line => {
                  gsap.set(line, { display: "inline" })
                })
              }, 200) // 200ms delay after last animation completes
            }
          : undefined,
      })
    })
  }, [inView, shouldAnimateIn, split])

  return (
    <span
      className={`text-lockup-v2 ${className} ${
        shouldAnimateIn ? "tl-prep" : ""
      }`}
      ref={ref}
      style={{ opacity: 1 }}
    >
      <span className="text-lockup-v2__content" ref={contentRef}>
        {children}
      </span>
    </span>
  )
}

TextLockupV2.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  shouldAnimateIn: PropTypes.bool,
}

export default TextLockupV2
